import React from 'react';
import { CircleCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { APPLICATION_TYPE } from '../../../my-products/models/product.model';

interface ApplicationTypeStepProps {
  selectedTypes: APPLICATION_TYPE[];
  onToggle: (type: APPLICATION_TYPE) => void;
}

/** Floor icon — isometric box with highlighted bottom face */
const FloorSvgIcon: React.FC = () => (
  <svg width="120" height="96" viewBox="11 0 42 34" fill="none">
    <path d="M32 4L45 11L45 24L32 31L19 24L19 11L32 4ZM32 4V17" stroke="currentColor" strokeOpacity="0.3" strokeLinejoin="round" strokeWidth="0.7"/>
    <path d="M32 17L45 24L32 31L19 24L32 17Z" fill="url(#fl_g)" stroke="currentColor" strokeOpacity="0.6" strokeLinejoin="round" strokeWidth="0.9" strokeDasharray="2 1.5"/>
    <defs>
      <linearGradient id="fl_g" x1="32" y1="17" x2="32" y2="31" gradientUnits="userSpaceOnUse">
        <stop stopColor="currentColor" stopOpacity="0.05"/>
        <stop offset="1" stopColor="currentColor" stopOpacity="0.22"/>
      </linearGradient>
    </defs>
  </svg>
);

/** Walls icon — isometric box with highlighted side faces */
const WallsSvgIcon: React.FC = () => (
  <svg width="120" height="96" viewBox="11 0 42 34" fill="none">
    <path d="M32 31L45 24L45 11L32 4L19 11L19 24L32 31Z" stroke="currentColor" strokeOpacity="0.3" strokeLinejoin="round" strokeWidth="0.7"/>
    <path d="M45 11L32 4V17L45 24L45 11Z" fill="url(#wl_gr)" stroke="currentColor" strokeOpacity="0.6" strokeLinejoin="round" strokeWidth="0.9" strokeDasharray="2 1.5"/>
    <path d="M32 4L19 11L19 24L32 17V4Z" fill="url(#wl_gl)" stroke="currentColor" strokeOpacity="0.6" strokeLinejoin="round" strokeWidth="0.9" strokeDasharray="2 1.5"/>
    <defs>
      <linearGradient id="wl_gr" x1="45" y1="14" x2="32" y2="14" gradientUnits="userSpaceOnUse">
        <stop stopColor="currentColor" stopOpacity="0.22"/>
        <stop offset="1" stopColor="currentColor" stopOpacity="0.05"/>
      </linearGradient>
      <linearGradient id="wl_gl" x1="19" y1="15" x2="32" y2="15" gradientUnits="userSpaceOnUse">
        <stop stopColor="currentColor" stopOpacity="0.22"/>
        <stop offset="1" stopColor="currentColor" stopOpacity="0.05"/>
      </linearGradient>
    </defs>
  </svg>
);

const APPLICATION_OPTIONS: { type: APPLICATION_TYPE; labelKey: string; Icon: React.FC }[] = [
  { type: APPLICATION_TYPE.FLOOR, labelKey: 'PAGES.NEW_PRODUCT.GENERAL_INFO.FIELDS.FLOOR', Icon: FloorSvgIcon },
  { type: APPLICATION_TYPE.WALLS, labelKey: 'PAGES.NEW_PRODUCT.GENERAL_INFO.FIELDS.WALLS', Icon: WallsSvgIcon },
];

export const ApplicationTypeStep: React.FC<ApplicationTypeStepProps> = ({
  selectedTypes,
  onToggle,
}) => {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-2 gap-4">
      {APPLICATION_OPTIONS.map(({ type, labelKey, Icon }) => {
        const isSelected = selectedTypes.includes(type);

        return (
          <div
            key={type}
            data-testid={`app-type-card-${type}`}
            onClick={() => onToggle(type)}
            className={cn(
              'relative flex h-[180px] cursor-pointer flex-col items-center justify-center gap-3 overflow-hidden border-[1.5px] bg-card transition-colors',
              isSelected ? 'border-foreground' : 'border-border hover:border-foreground/40'
            )}
          >
            {/* Selected check icon */}
            {isSelected && (
              <CircleCheck className="absolute top-2.5 end-2.5 size-[18px] text-foreground" />
            )}

            <span className={isSelected ? 'text-foreground' : 'text-muted-foreground'}>
              <Icon />
            </span>

            <p className="text-sm font-medium text-foreground">{t(labelKey)}</p>
          </div>
        );
      })}
    </div>
  );
};
